import { ChangeDetectorRef, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, defaultIfEmpty, take } from 'rxjs/operators';
import { DashboardBulkSelectionService } from '../../../services/dashboard-bulk-selection.service';
import { IConfigSlider } from '../../../classes/custom-range-slider';
import { AuthenticationService } from '../../../services/authentication.service';
import { DashboardSearchBase } from '../common/dashboard-search-base';
import {
  inferFsPlay, isActiveFsFilterTag, FS_ASSET_SLIDERS, FS_COMMON_FINANCIAL_SLIDERS, FS_EPC_SLIDERS,
  FS_FILTER_LABELS, FS_PAF_FILTER_KEYS, FS_PLAY_FINANCIAL_SLIDERS, FS_PLAYS,
  FsFilterTag, FsPlayId, fsWorkspaceFilterKeys, unsupportedFsQueryKeys,
} from './financial-services-dashboard.config';
import { fsMagicCategory, FsMagicCategory, FS_MAGIC_MODULE_NAME } from './fs-magic-relevance';
import { fsFilterHelpText as getFsFilterHelpText } from './fs-filter-help';

const FS_NEWS_SOURCE_VALUES = new Set(['external', 'websiteNews', 'selfPublished', 'either']);

interface FsMagicFilterGroup {
  questionId: number;
  title: string;
  category: FsMagicCategory;
  filters: Record<string, any>[];
  unavailableFilters: Record<string, any>[];
}

interface FsMagicModule {
  id: number;
  name: string;
  hasFilterAccess: boolean;
  groups: FsMagicFilterGroup[];
}

interface FsMagicFilterIdentity {
  moduleId: number;
  questionId: number;
  fieldName: string;
  filterType: string;
  label: string;
  available: boolean;
}

type FsResultView = 'overview' | 'credit' | 'fx_trade' | 'lending' | 'working_capital' | 'ownership' | 'cash_treasury';

interface FsResultInsight {
  questionId: number;
  fieldKey: string;
  label: string;
  value: unknown;
  dateGenerated: string | null;
  confidence: number | null;
  source: string;
  estimated: boolean;
}

const FS_RESULT_VIEWS: ReadonlyArray<{ id: FsResultView; label: string; icon: string; description: string }> = [
  { id: 'overview', label: 'Overview', icon: 'fa-grid-2', description: 'Company size, status, banker and recorded advisers' },
  { id: 'credit', label: 'Credit', icon: 'fa-gauge', description: 'Stored credit appetite and stress signals' },
  { id: 'fx_trade', label: 'FX & trade', icon: 'fa-globe', description: 'Stored currency, hedging and trade finance signals' },
  { id: 'lending', label: 'Lending', icon: 'fa-building-columns', description: 'Stored borrowing, maturity and product need signals' },
  { id: 'working_capital', label: 'Working capital', icon: 'fa-arrows-rotate', description: 'Stored customer terms, assets and capex signals' },
  { id: 'ownership', label: 'Ownership', icon: 'fa-sitemap', description: 'Stored ownership, exit and acquisition signals' },
  { id: 'cash_treasury', label: 'Cash & treasury', icon: 'fa-vault', description: 'Stored surplus cash and treasury signals' },
];

/** Focused search surface over the full dashboard's existing filters. */
@Component({
  selector: 'app-financial-services-dashboard',
  templateUrl: './financial-services-dashboard.component.html',
  styleUrls: ['./financial-services-dashboard.component.css'],
  standalone: false,
  providers: [DashboardBulkSelectionService],
})
export class FinancialServicesDashboardComponent extends DashboardSearchBase {
  @ViewChild('fsSearchPane') private fsSearchPane?: ElementRef<HTMLElement>;
  private readonly fsRoute = inject(ActivatedRoute);
  private readonly fsRouter = inject(Router);
  private readonly fsCdr = inject(ChangeDetectorRef);
  private readonly fsAuthenticationService = inject(AuthenticationService);
  readonly fsPlays = FS_PLAYS;
  readonly fsResultViews = FS_RESULT_VIEWS;
  fsResultView: FsResultView = 'overview';

  get fsResultViewDetails(): typeof FS_RESULT_VIEWS[number] {
    return this.fsResultViews.find(view => view.id === this.fsResultView) || this.fsResultViews[0];
  }

  /** The profile opens on the evidence the reader was comparing, otherwise on the active filter view. */
  get fsProfileView(): string {
    if (this.fsResultView === 'credit') return 'credit_screen';
    return this.fsResultView === 'overview' ? this.activeFsPlay : this.fsResultView;
  }
  readonly fsFilterLabels = FS_FILTER_LABELS;
  readonly fsFilterHelpText = getFsFilterHelpText;
  readonly fsCommonFinancialSliders = FS_COMMON_FINANCIAL_SLIDERS;
  readonly fsEpcSliders = FS_EPC_SLIDERS;
  readonly fsAssetSliders = FS_ASSET_SLIDERS;
  readonly fsAllFinancialSliders = Object.values(FS_PLAY_FINANCIAL_SLIDERS).flat()
    .filter((definition, index, all) =>
      all.findIndex(candidate => candidate.key === definition.key) === index);
  fsFinancialMeasureQuery = '';
  fsCompanyStatusQuery = '';
  fsCompanyCategoryQuery = '';
  fsUkRegionQuery = '';
  fsSicMode: 'include' | 'exclude' = 'include';
  fsPostcodeMode: 'prefix' | 'district' | 'radius' = 'prefix';
  fsMagicModules: FsMagicModule[] = [];
  private fsMagicFilterKeys = new Map<string, FsMagicFilterIdentity>();
  activeFsPlay: FsPlayId = 'fx_trade';
  fsHasSearched = false;
  fsActiveOnly = true;
  fsDefaultState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  fsSaveOpen = false;
  private fsAppliedFilterSignature = '';
  private fsCriteriaRefreshTimer?: ReturnType<typeof setTimeout>;
  private fsSafeInitialQueryParams?: Record<string, any>;

  override ngOnInit(): void {
    const queryParams = this.fsRoute.snapshot.queryParams;
    const pafKeys = Object.keys(queryParams).filter(key => FS_PAF_FILTER_KEYS.has(key));
    if (pafKeys.length) {
      // PAF access is loaded asynchronously. Resolve it before restoring a
      // shared search, so an unlicensed land/EPC criterion cannot auto-submit.
      this.fsAuthenticationService.getPlanFeaturesEnabled().pipe(
        take(1),
        defaultIfEmpty({ has_paf_access: false }),
        catchError(() => of({ has_paf_access: false })),
      ).subscribe((features: any) => {
        const hasPAFAccess = features?.has_paf_access === true;
        const safeParams = { ...queryParams };
        if (!hasPAFAccess) {
          pafKeys.forEach(key => delete safeParams[key]);
          this.fsSafeInitialQueryParams = safeParams;
        }
        if (this.initialiseFsFromQueryParams(safeParams, hasPAFAccess) && !hasPAFAccess) {
          this.fsRouter.navigate(['/dashboard/financial-services'], {
            queryParams: safeParams, replaceUrl: true,
          });
        }
      });
      return;
    }
    this.initialiseFsFromQueryParams(queryParams, false);
  }

  private initialiseFsFromQueryParams(
    queryParams: Record<string, any>, hasPAFAccess: boolean
  ): boolean {
    const requestedPlay = queryParams['play'];
    const validPlay = this.fsPlays.some(play => play.id === requestedPlay);
    const inferredPlay = inferFsPlay(queryParams);
    if (requestedPlay && !validPlay) {
      this.openUnsupportedSearchInFullDashboard(queryParams);
      return false;
    }
    this.activeFsPlay = validPlay ? requestedPlay as FsPlayId : inferredPlay || 'fx_trade';
    if (unsupportedFsQueryKeys(queryParams, this.activeFsPlay, hasPAFAccess)
      .some(key => !this.isPotentialFsMagicQuery(key, queryParams[key])) ||
        String(queryParams['postcodeBeginsWith'] || '').includes(',') ||
        !this.validFsStatusQuery(queryParams['selectedCompanyStatuses']) ||
        !this.validFsNewsSourceQuery(queryParams['newsSource']) ||
        String(queryParams['upperDaysUntilFinancialYearEnd'] || '') === '0') {
      this.openUnsupportedSearchInFullDashboard(queryParams);
      return false;
    }
    if (hasPAFAccess) this.hasPAFAccess = true;
    super.ngOnInit();
    if (hasPAFAccess) this.hasPAFAccess = true;
    return true;
  }

  override initialiseBasedUponGetParamsIfAllLoaded(): void {
    // Strip presentation keys before the base interprets URL keys as search
    // filters. In particular, bare ?play=energy must stay an empty workspace.
    const { play, resultsMode, pageNumber, ...searchParams } =
      this.fsSafeInitialQueryParams ?? this.urlQueryParams;
    this.fsSafeInitialQueryParams = undefined;
    if (Object.entries(searchParams).some(([param, value]) =>
      this.isPotentialFsMagicQuery(param, value) &&
      !this.isActiveFsMagicTag({ param, value }) &&
      this.isNonemptyFsMagicValue(value))) {
      this.openUnsupportedSearchInFullDashboard(searchParams);
      return;
    }
    const activeParams = Object.fromEntries(Object.entries(searchParams)
      .filter(([param, value]) =>
        isActiveFsFilterTag({ param, value }) ||
        this.isActiveFsMagicTag({ param, value })));
    if (activeParams['newsSource'] === 'either') {
      // The full dashboard's legacy "either" means the default Any source.
      delete activeParams['newsSource'];
    }
    if (!activeParams['lowerRevenue'] && !activeParams['upperRevenue']) {
      delete activeParams['ignoreRevenueEstimates'];
    }
    const hasCriterion = Object.keys(activeParams).some(param =>
      param !== 'selectedCompanyStatuses' &&
      (param !== 'selectedTerritory' || !this.areTerritoriesEnforced)
    );
    this.urlQueryParams = hasCriterion ? activeParams : {};
    if (!activeParams['selectedCompanyStatuses']) this.selectActiveCompaniesInUI();
    super.initialiseBasedUponGetParamsIfAllLoaded();
    this.fsPostcodeMode = this.postcodeWithinRadius ? 'radius'
      : this.postcodeDistrict ? 'district' : 'prefix';
    this.fsActiveOnly = (this.companyStatusOptions || []).some(option =>
      option.checked && option.status_clean === 'Active Companies') &&
      (this.companyStatusOptions || []).filter(option => option.checked).length === 1;
    this.refreshSearchTagsFromCurrentFilters();
  }

  private validFsStatusQuery(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    try {
      const selected = JSON.parse(String(value));
      return Array.isArray(selected) && selected.length > 0 &&
        selected.every(status => typeof status === 'string' && status.length < 80);
    } catch (_) {
      return false;
    }
  }

  private validFsNewsSourceQuery(value: unknown): boolean {
    return value === undefined || value === null || value === '' ||
      (typeof value === 'string' && FS_NEWS_SOURCE_VALUES.has(value));
  }

  private openUnsupportedSearchInFullDashboard(params: Record<string, any>): void {
    const { play, resultsMode, ...fullQueryParams } = params;
    this.fsRouter.navigate(['/dashboard'], { queryParams: fullQueryParams });
  }

  get fsSearchLoading(): boolean {
    return this.isLoading().loadingCompanies || this.isLoading().loadingCompanyCount;
  }

  get fsPostcodeValid(): boolean {
    const singlePostcode = [this.postcodeBeginsWith, this.postcodeDistrict, this.postcodeWithinRadius]
      .every(value => !String(value || '').includes(','));
    return singlePostcode && (!this.postcodeWithinRadius ||
      Number(this.mileRadiusOfPostcode) > 0);
  }

  get fsYearEndValid(): boolean {
    const lower = this.lowerDaysUntilFinancialYearEnd;
    const upper = this.upperDaysUntilFinancialYearEnd;
    const valid = (value: number | null) => value === null || value === undefined ||
      (Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 365);
    return valid(lower) && valid(upper) &&
      (upper === null || upper === undefined || Number(upper) >= 1) &&
      (lower === null || upper === null || lower === undefined || upper === undefined ||
        Number(lower) <= Number(upper));
  }

  get fsNewsDaysValid(): boolean {
    const value = this.newsWithinDays;
    if (value === null || value === undefined || String(value).trim() === '') return true;
    return Number.isFinite(Number(value)) && Number(value) >= 1;
  }

  get fsActiveTags(): FsFilterTag[] {
    const visible = fsWorkspaceFilterKeys();
    return (this.searchTags || []).filter(tag =>
      ((visible.has(tag.param) && isActiveFsFilterTag(tag)) ||
        this.isActiveFsMagicTag(tag)) &&
      (tag.param !== 'newsSource' ||
        (this.validFsNewsSourceQuery(tag.value) && tag.value !== 'either')) &&
      (tag.param !== 'ignoreRevenueEstimates' || this.fsHasRevenueRange)
    );
  }

  get fsHasRevenueRange(): boolean {
    const range = this.sliderValues?.['Revenue'];
    return range?.lower !== null && range?.lower !== undefined ||
      range?.upper !== null && range?.upper !== undefined;
  }

  get fsFilterCount(): number {
    return this.fsActiveTags.filter(tag => tag.param !== 'selectedCompanyStatuses').length;
  }

  get fsHasSearchCriterion(): boolean {
    return this.fsActiveTags.some(tag =>
      tag.param !== 'selectedCompanyStatuses' &&
      tag.param !== 'ignoreRevenueEstimates' &&
      (tag.param !== 'selectedTerritory' || !this.areTerritoriesEnforced)
    );
  }

  get fsHasPendingChanges(): boolean {
    return this.fsHasSearched &&
      this.fsFilterSignature() !== this.fsAppliedFilterSignature;
  }

  private fsFilterSignature(): string {
    return JSON.stringify(this.fsActiveTags
      .map(tag => ({ param: tag.param, value: String(tag.value) }))
      .sort((a, b) => a.param.localeCompare(b.param)));
  }

  /** The config endpoint is the source of truth for entitled question fields. */
  override updateAiraFilterDetails(data: Record<string, any>[]): void {
    // The focused workspace exposes the shared Financial Services module only,
    // which also carries the shared FX questions. Private organisation modules
    // remain available in the Full dashboard. Filter before the base builds its
    // search state so another module cannot be retained invisibly through a
    // saved or shared URL.
    const publicFsModules = (data || []).filter(module =>
      module.sectionName === FS_MAGIC_MODULE_NAME && module.isOrganisationModule !== true);
    // The base stores values by label. Use the question and field identity so
    // two same-labelled fields cannot overwrite each other. Remove free-text
    // reasoning fields before the base can build searchable controls.
    const modules: Record<string, any>[] = publicFsModules.map(module => ({
      ...module,
      questions: (module.questions || []).map(question => ({
        ...question,
        filters: Object.fromEntries(Object.entries(question.filters || {})
          .map(([groupKey, filters]: [string, any[]]) => [groupKey,
            filters.filter(filter => !/(^|__)reasoning$/.test(filter.fieldName || ''))
              .map(filter => ({
                ...filter,
                fsDisplayLabel: filter.label,
                label: `MI Q${question.id} ${filter.fieldName}`,
              })),
          ])),
      })).filter(question => Object.values(question.filters)
        .some((filters: any[]) => filters.length > 0)),
    }));
    super.updateAiraFilterDetails(modules);
    this.fsMagicFilterKeys.clear();
    this.fsMagicModules = modules.map(module => {
      const groups = (module.questions || []).map(question => {
        const filters = Object.values(question.filters || {}).flat() as Record<string, any>[];
        const availableFilters = filters.filter(filter => this.fsMagicUnavailableReason(filter) === '');
        const unavailableFilters = filters.filter(filter => this.fsMagicUnavailableReason(filter) !== '');
        filters.forEach(filter => {
          const identity: FsMagicFilterIdentity = {
            moduleId: module.id,
            questionId: question.id,
            fieldName: filter.fieldName,
            filterType: filter.filterType,
            label: filter.fsDisplayLabel,
            available: Boolean(module.hasFilterAccess) && this.fsMagicUnavailableReason(filter) === '',
          };
          for (const key of filter.filterType === 'range_slider'
            ? [`lower${filter.label}`, `upper${filter.label}`] : [filter.label]) {
            this.fsMagicFilterKeys.set(key, identity);
          }
        });
        return {
          questionId: question.id,
          title: this.fsMagicQuestionTitle(module.sectionName, question.id, filters),
          category: fsMagicCategory(module.sectionName, question.id, filters),
          filters: availableFilters,
          unavailableFilters,
        };
      }).filter(group => group.filters.length || group.unavailableFilters.length);
      return {
        id: module.id,
        name: module.sectionName,
        hasFilterAccess: Boolean(module.hasFilterAccess),
        groups,
      };
    }).filter(module => module.groups.length);
  }

  fsMagicModulesForCategory(category: FsMagicCategory): FsMagicModule[] {
    return this.fsMagicModules
      .map(module => ({
        ...module,
        groups: module.groups.filter(group => group.category === category),
      }))
      .filter(module => module.groups.length > 0);
  }

  fsMagicQuestionCount(category: FsMagicCategory): number {
    return this.fsMagicModulesForCategory(category)
      .reduce((count, module) => count + module.groups.length, 0);
  }

  fsVisibleTeamTags(options: any[]): any[] {
    const selected = options.filter(tag => tag.checked);
    const matches = options.filter(tag => !tag.checked && tag.shouldShow !== false);
    return [...selected, ...matches.slice(0, 30)];
  }

  override filterTags(): void {
    const query = this.tagSearchTerm.trim().toLocaleLowerCase();
    for (const tag of [...this.tagsPositiveOptions, ...this.tagsNegativeOptions]) {
      tag.shouldShow = !query || String(tag.name || '').toLocaleLowerCase().includes(query);
    }
  }

  fsMagicQuestionTitle(moduleName: string, id: number, filters: Record<string, any>[]): string {
    // Question IDs differ between environments. The module's field names are
    // the stable identity for headings.
    const fieldTitles: Array<[RegExp, string]> = [
      [/^(estimated_credit_worthiness_score)/, 'AI credit score'],
      [/^(stress_level|stress_signals__)/, 'Credit stress signals'],
      [/^(estimated_annual_fx_volume|currency_flows__|flow_types|natural_hedge)/, 'FX flows'],
      [/^(fx_opportunity_score|fx_risk_level|hedging_status|fx_and_payment_providers__|recommended_fx_products|hedging_instruments__)/, 'Hedging and FX providers'],
      [/^(trade_finance_likelihood_score|likely_trade_instruments|trade_flows__|influencing_factors)/, 'Trade finance'],
      [/^(currency_volume_distribution__|currency_distribution__)/, 'Currency exposure'],
      [/^countries_broken_down_by_/, 'International operations'],
      [/^(facilities__|earliest_maturity_year|total_disclosed_borrowings_gbp)/, 'Debt and facilities'],
      [/^(needs__|top_need)/, 'Financial product needs'],
      [/^(b2b_revenue_percentage|customer_types|customer_concentration|typical_customer_payment_terms_days|seasonality_score|peak_trading_months|working_capital_pressures__)/, 'Customers and working capital'],
      [/^(key_assets__|planned_investments__|asset_finance_likelihood_score|capex_next_12_months_)/, 'Assets and capex'],
      [/^(ownership_type|sale_likelihood_score|likely_transaction|succession_signals)/, 'Ownership and exit'],
      [/^(acquisition_likelihood_score|recent_acquisitions__|growth_plans__)/, 'Acquisitions and growth'],
      [/^(immediate_parent_orgs__|ultimate_beneficial_owners__|summary_of_group_structure)/, 'Group structure'],
      [/^events__/, 'Trigger events'],
      [/^(surplus_cash_assessment|treasury_sophistication|treasury_summary|treasury_needs|finance_decision_makers__|open_finance_roles__)/, 'Cash, treasury and finance team'],
    ];
    if (moduleName === FS_MAGIC_MODULE_NAME) {
      const titles = new Set(filters.map(filter =>
        fieldTitles.find(([pattern]) => pattern.test(filter.fieldName))?.[1]
      ).filter((title): title is string => Boolean(title)));
      if (titles.size === 1) return [...titles][0];
    }
    const distinctive = filters.find(filter =>
      filter.fsDisplayLabel && !/^reasoning contains$/i.test(filter.fsDisplayLabel));
    return distinctive?.fsDisplayLabel || `Research question ${id}`;
  }

  fsMagicUnavailableReason(filter: Record<string, any>): string {
    if (!['range_slider', 'text_input', 'multi_select'].includes(filter.filterType)) {
      return 'This filter type is not available in search.';
    }
    return '';
  }

  private isPotentialFsMagicQuery(param: string, value: unknown): boolean {
    if (param === 'selectedModules') return false;
    try {
      const field = JSON.parse(String(value));
      return Number.isInteger(Number(field.airaQuestionId)) &&
        typeof field.fieldName === 'string' && !!field.fieldName &&
        ['range_slider', 'text_input', 'multi_select'].includes(field.filterType) &&
        (field.filterType === 'range_slider'
          ? (param.startsWith('lower') || param.startsWith('upper'))
          : !param.startsWith('lower') && !param.startsWith('upper'));
    } catch (_) { return false; }
  }

  private isNonemptyFsMagicValue(value: unknown): boolean {
    try {
      const parsed = JSON.parse(String(value));
      return parsed.value !== null && parsed.value !== undefined &&
        parsed.value !== '' && (!Array.isArray(parsed.value) || parsed.value.length > 0);
    } catch (_) { return false; }
  }

  private isActiveFsMagicTag(tag: FsFilterTag): boolean {
    if (!this.isPotentialFsMagicQuery(tag.param, tag.value) ||
        !this.isNonemptyFsMagicValue(tag.value)) return false;
    const identity = this.fsMagicFilterKeys.get(tag.param);
    if (!identity?.available) return false;
    const value = JSON.parse(String(tag.value));
    return Number(value.airaQuestionId) === identity.questionId &&
      value.fieldName === identity.fieldName && value.filterType === identity.filterType;
  }

  fsFilterLabel(tag: FsFilterTag): string {
    const magic = this.fsMagicFilterKeys.get(tag.param);
    return magic ? `${magic.label} · ${this.fsMagicModules.find(module => module.id === magic.moduleId)?.name || 'Magic Insights'}`
      : this.fsFilterLabels[tag.param] || tag.param;
  }

  updateFsMagicSlider(values: Record<string, any>, filter: Record<string, any>): void {
    this.triggerConfigSliderWithObjectUpdate(values, filter);
    this.queueFsCriteriaRefresh();
  }

  updateFsMagicText(value: string, filter: Record<string, any>): void {
    this.triggerConfigTextInputWithObjectUpdate(value, filter);
    this.queueFsCriteriaRefresh();
  }

  updateFsMagicOptions(filter: Record<string, any>): void {
    this.triggerConfigMultiSelectInputWithObjectUpdate(filter);
    this.queueFsCriteriaRefresh();
  }

  updateFsMagicConfidence(values: Record<string, any>, filter: Record<string, any>): void {
    this.triggerConfidenceSliderUpdate(values, filter);
    this.queueFsCriteriaRefresh();
  }

  queueFsCriteriaRefresh(): void {
    clearTimeout(this.fsCriteriaRefreshTimer);
    this.fsCriteriaRefreshTimer = setTimeout(() => {
      this.normalizeFsDependencies();
      this.refreshSearchTagsFromCurrentFilters();
      // This timer can run outside Angular's change-detection turn. Update the
      // button binding now so a newly entered criterion enables the search.
      this.fsCdr.detectChanges();
    }, 50);
  }

  private normalizeFsDependencies(): void {
    if (!this.validFsNewsSourceQuery(this.newsSource) || this.newsSource === 'either') {
      this.newsSource = '';
    }
    if (!this.fsHasRevenueRange) this.ignoreRevenueEstimates = false;
  }

  runFsSearch(): void {
    if (!this.fsPostcodeValid || !this.fsYearEndValid || !this.fsNewsDaysValid || this.fsSearchLoading ||
        this.isLoading().loadingUI || !this.allEndpointsLoaded() || this.isReadOnlyUser) return;
    this.normalizeFsDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    if (!this.fsHasSearchCriterion) return;
    // An open filter group uses the full canvas for editing. Return to the
    // compact two-pane layout when the user asks to see the search results.
    this.fsSearchPane?.nativeElement.querySelectorAll<HTMLDetailsElement>('.fs-dashboard__group[open]')
      .forEach(group => { group.open = false; });
    this.searchCompaniesAfterFilters(true);
  }

  override searchCompaniesAfterFilters(initialisePageCounter: boolean): void {
    this.normalizeFsDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    this.fsHasSearched = true;
    super.searchCompaniesAfterFilters(initialisePageCounter);
    this.fsAppliedFilterSignature = this.fsFilterSignature();
    this.syncFsRouteFromCurrentFilters();
  }

  selectFsPlay(play: FsPlayId): void {
    if (play === this.activeFsPlay) return;
    // Service selection changes profile context only. Every grouped filter
    // remains visible and selected, including cross-service combinations.
    this.activeFsPlay = play;
    this.syncFsRouteFromCurrentFilters();
    this.fsCdr.markForCheck();
  }

  private clearFsArraySelection(key: string): boolean {
    const optionsByKey: Record<string, any[]> = {
      selectedFinancialYearEnds: this.financialYearEndsOptions,
      selectedOfficeCountries: this.countriesOfOfficesOptions,
      selectedGeographicalRevenueCountries: this.countriesOfGeographicalRevenueOptions,
      selectedGeographicalRevenueTerritories: this.territoriesOfGeographicalRevenueOptions,
      selectedImportExportCategories: this.importExportCategories,
    };
    if (!Object.prototype.hasOwnProperty.call(optionsByKey, key)) return false;
    this[key] = [];
    (optionsByKey[key] || []).forEach(option => option.checked = false);
    return true;
  }

  resetFsFilters(preserveContext = false): void {
    const name = preserveContext ? this.companyNameContains : '';
    const territory = preserveContext || this.areTerritoriesEnforced
      ? this.selectedTerritory : null;
    this.resetAllFiltersAndUpdateFilters();
    this.companyNameContains = name;
    this.selectedTerritory = territory;
    this.postcodeBeginsWith = '';
    this.accountsSearchTerm = '';
    this.newsContaining = '';
    this.newsWithinDays = null;
    this.newsSource = '';
    this.homepageSearchTerm = '';
    this.hiringJobTitleContains = '';
    this.recentDirectorshipTitleContains = '';
    this.accountantNameContains = '';
    this.auditorNameContains = '';
    this.bankerNameContains = '';
    this.selectedAccountants = [];
    this.selectedAuditors = [];
    this.selectedBankers = [];
    this.bankerSearchInput = '';
    this.possibleBankers = [];
    this.noOutstandingCharges = false;
    this.chargeRegistered = false;
    this.outstandingChargeRegistered = false;
    this.chargeHolderNameContains = '';
    this.ignoreSatisfiedCharges = false;
    this.chargeCreatedMonthsAgoLower = null;
    this.chargeCreatedMonthsAgoUpper = null;
    this.selectedLatestAccountsAuditOption = '';
    this.accountsAreOverdue = false;
    this.hasAnyPatentRecord = false;
    this.hasRaisedInvestment = false;
    this.issuedAGrant = false;
    this.hasLandTitle = false;
    this.ignoreRevenueEstimates = false;
    this.excludeCompaniesTaggedByTeam = false;
    this.fsActiveOnly = true;
    Object.keys({
      selectedFinancialYearEnds: true,
      selectedOfficeCountries: true,
      selectedGeographicalRevenueCountries: true,
      selectedGeographicalRevenueTerritories: true,
      selectedImportExportCategories: true,
    }).forEach(key => this.clearFsArraySelection(key));
    this.selectActiveCompaniesInUI();
    this.fsHasSearched = false;
    this.fsAppliedFilterSignature = '';
    this.companies = [];
    this.pageNumber = 1;
    this.sicCodeTextSearch = '';
    this.fsFinancialMeasureQuery = '';
    this.fsUkRegionQuery = '';
    this.fsSicMode = 'include';
    this.fsPostcodeMode = 'prefix';
    this.refreshSearchTagsFromCurrentFilters();
    this.syncFsRouteFromCurrentFilters();
    this.fsCdr.markForCheck();
  }

  private syncFsRouteFromCurrentFilters(): void {
    const queryParams: Record<string, unknown> = { play: this.activeFsPlay };
    this.fsActiveTags.forEach(tag => queryParams[tag.param] = tag.value);
    // Writing the view and filters to the URL is not a page change, so keep the reader's scroll position.
    void this.fsRouter.navigate(['/dashboard/financial-services'], {
      queryParams,
      replaceUrl: true,
      scroll: 'manual',
    });
  }

  setFsActiveOnly(value: boolean): void {
    this.fsActiveOnly = value;
    (this.companyStatusOptions || []).forEach(option => {
      option.checked = value && option.status_clean === 'Active Companies';
    });
    this.queueFsCriteriaRefresh();
  }

  setFsStatusOption(option: Record<string, any>, selected: boolean): void {
    option.checked = selected;
    this.fsActiveOnly = (this.companyStatusOptions || []).some(status =>
      status.checked && status.status_clean === 'Active Companies') &&
      (this.companyStatusOptions || []).filter(status => status.checked).length === 1;
    this.queueFsCriteriaRefresh();
  }

  get fsFilteredCompanyStatusOptions(): Array<any> {
    const query = this.fsCompanyStatusQuery.trim().toLocaleLowerCase();
    if (!query) return this.companyStatusOptions || [];
    return (this.companyStatusOptions || []).filter(option =>
      String(option.status_clean || '').toLocaleLowerCase().includes(query));
  }

  get fsFilteredCompanyCategoryOptions(): Array<any> {
    const query = this.fsCompanyCategoryQuery.trim().toLocaleLowerCase();
    if (!query) return this.companyCategoryOptions || [];
    return (this.companyCategoryOptions || []).filter(option =>
      String(option.name || '').toLocaleLowerCase().includes(query));
  }

  get fsFilteredUkRegions(): Array<any> {
    const query = this.fsUkRegionQuery.trim().toLocaleLowerCase();
    if (!query) return this.ukRegions || [];
    return (this.ukRegions || []).filter(region =>
      String(region.name || '').toLocaleLowerCase().includes(query));
  }

  setFsPostcodeMode(mode: 'prefix' | 'district' | 'radius'): void {
    this.fsPostcodeMode = mode;
    if (mode !== 'prefix') this.postcodeBeginsWith = '';
    if (mode !== 'district') this.postcodeDistrict = '';
    if (mode !== 'radius') {
      this.postcodeWithinRadius = '';
      this.mileRadiusOfPostcode = null;
    } else if (this.mileRadiusOfPostcode === null || this.mileRadiusOfPostcode === undefined) {
      this.mileRadiusOfPostcode = 10;
    }
    this.updateAddressTypeOnInputChange();
    this.queueFsCriteriaRefresh();
  }

  setFsRadiusMiles(event: Event): void {
    this.mileRadiusOfPostcode = Number((event.target as HTMLInputElement).value);
    this.queueFsCriteriaRefresh();
  }

  normalizeFsRadiusMiles(): void {
    const value = Number(this.mileRadiusOfPostcode);
    this.mileRadiusOfPostcode = Number.isFinite(value) && value > 0
      ? Math.min(500, Math.max(1, Math.round(value))) : 10;
    this.queueFsCriteriaRefresh();
  }

  updateFsChargeSelection(changed: 'noOutstandingCharges' | 'outstandingChargeRegistered'): void {
    if (changed === 'noOutstandingCharges' && this.noOutstandingCharges) {
      this.outstandingChargeRegistered = false;
    } else if (changed === 'outstandingChargeRegistered' && this.outstandingChargeRegistered) {
      this.noOutstandingCharges = false;
    }
    this.queueFsCriteriaRefresh();
  }

  get fsExcludedSics(): Record<string, any>[] {
    return (this.companySICCodeOptions || []).filter(option => option.excluded);
  }

  get fsSicSuggestions(): Record<string, any>[] {
    if ((this.sicCodeTextSearch || '').trim().length < 3) return [];
    return (this.companySICCodeOptions || [])
      .filter(option => option.shouldShow && option.identifier)
      .slice(0, 12);
  }

  get fsSelectedSics(): Record<string, any>[] {
    return (this.companySICCodeOptions || []).filter(option => option.checked);
  }

  setFsSic(option: Record<string, any>, selected: boolean): void {
    if (this.fsSicMode === 'include') {
      option.checked = selected;
      if (selected) option.excluded = false;
    } else {
      option.excluded = selected;
      if (selected) option.checked = false;
    }
    this.queueFsCriteriaRefresh();
  }

  removeFsSic(option: Record<string, any>, mode: 'include' | 'exclude'): void {
    option[mode === 'include' ? 'checked' : 'excluded'] = false;
    this.queueFsCriteriaRefresh();
  }

  fsSlider(label: string): IConfigSlider | undefined {
    return Object.values(this.zintSlidersConfigs || {})
      .flat()
      .find((slider: IConfigSlider) => slider.label === label);
  }

  fsFinancialSliderAvailable(label: string): boolean {
    if (['Title Price Paid', 'Ownership Age Months'].includes(label)) return this.hasPAFAccess;
    if (label.startsWith('Esos')) return this.hasEnergyAndEmissionsAccess;
    return true;
  }

  matchesFsFinancialMeasure(definition: { key: string; label: string }): boolean {
    const query = this.fsFinancialMeasureQuery.trim().toLowerCase();
    return !query || definition.label.toLowerCase().includes(query) ||
      definition.key.toLowerCase().includes(query);
  }

  get fsFinancialMeasureCount(): number {
    return this.fsAllFinancialSliders.filter(definition =>
      this.fsFinancialSliderAvailable(definition.key) && !!this.fsSlider(definition.key)).length;
  }

  get fsMatchingFinancialMeasureCount(): number {
    return this.fsAllFinancialSliders.filter(definition =>
      this.fsFinancialSliderAvailable(definition.key) && !!this.fsSlider(definition.key) &&
      this.matchesFsFinancialMeasure(definition)).length;
  }

  updateFsSlider(event: Record<string, any>, label: string): void {
    this.triggerConfigSliderUpdate(event, label);
    this.queueFsCriteriaRefresh();
  }

  clearFsFilter(tag: FsFilterTag): void {
    const param = tag.param;
    if (this.fsMagicFilterKeys.has(param)) {
      this.clearFsMagicFilter(param);
    } else if (param === 'selectedCompanyStatuses') {
      this.setFsActiveOnly(false);
    } else if (param === 'selectedSICCodes') {
      (this.companySICCodeOptions || []).forEach(option => option.checked = false);
    } else if (param === 'excludedSICCodes') {
      (this.companySICCodeOptions || []).forEach(option => option.excluded = false);
    } else if (param === 'selectedCompanyCategories') {
      (this.companyCategoryOptions || []).forEach(option => option.checked = false);
    } else if (this.clearFsArraySelection(param)) {
      // Array-backed options must be unchecked before updateFilters rebuilds them.
    } else if (param === 'selectedAccountants' || param === 'selectedAuditors' || param === 'selectedBankers') {
      this[param] = [];
    } else if (param === 'tagPositiveIdsSelected' || param === 'tagNegativeIdsSelected') {
      const options = param === 'tagPositiveIdsSelected' ? this.tagsPositiveOptions : this.tagsNegativeOptions;
      (options || []).forEach(option => option.checked = false);
    } else if (param === 'selectedCustomColumns') {
      (this.customColumns || []).forEach(column => {
        (column.options || []).forEach(option => option.checked = false);
        column.dropdownInput = '';
        column.lockFilter = '';
        column.boxInputLower = null;
        column.boxInputUpper = null;
      });
    } else if (param === 'ukRegion') {
      (this.ukRegions || []).forEach(option => option.checked = false);
    } else if (param === 'selectedTerritory') {
      if (!this.areTerritoriesEnforced) this.selectedTerritory = null;
    } else if (param === 'lowerRevenue' || param === 'upperRevenue') {
      this.clearFsSlider('Revenue');
    } else if (param.includes('LandAndBuildingsAdditionsTangibleAssets')) {
      this.clearFsSlider('LandAndBuildingsAdditionsTangibleAssets');
    } else if (param.endsWith('Corporation Tax')) {
      this.clearFsSlider('Corporation Tax');
    } else if ((param.startsWith('lower') || param.startsWith('upper')) &&
               this.fsSlider(param.slice(5))) {
      this.clearFsSlider(param.slice(5));
    } else if (param === 'employeeLowerCount' || param === 'employeeUpperCount') {
      this.employeeLowerCount = null;
      this.employeeUpperCount = null;
      this.headcountSliderElement?.reset();
    } else if (param === 'lowerTotalEnergyKwhSelected' || param === 'upperTotalEnergyKwhSelected') {
      this.lowerTotalEnergyKwhSelected = null;
      this.upperTotalEnergyKwhSelected = null;
      this.totalEnergyKwhSliderElement?.reset();
    } else if (param === 'lowerGasKwhSelected' || param === 'upperGasKwhSelected') {
      this.lowerGasKwhSelected = null;
      this.upperGasKwhSelected = null;
      this.gasKwhSliderElement?.reset();
    } else if (param === 'lowerElectricKwhSelected' || param === 'upperElectricKwhSelected') {
      this.lowerElectricKwhSelected = null;
      this.upperElectricKwhSelected = null;
      this.electricKwhSliderElement?.reset();
    } else if (param === 'lowerPatentAwardedDays' || param === 'upperPatentAwardedDays') {
      this.lowerPatentAwardedDays = null;
      this.upperPatentAwardedDays = null;
      this.patentAwardedWithinDaysSliderElement?.reset();
    } else if (param === 'chargeCreatedMonthsAgoLower' || param === 'chargeCreatedMonthsAgoUpper') {
      this.chargeCreatedMonthsAgoLower = null;
      this.chargeCreatedMonthsAgoUpper = null;
      this.chargeCreatedSliderElement?.reset();
    } else if (typeof this[param] === 'boolean') {
      this[param] = false;
    } else if (typeof this[param] === 'number') {
      this[param] = null;
      // Keep the visual thumb in sync when a range endpoint is removed via a
      // filter chip. The search value remains null until the user moves it.
      const slider = this.keyToSliderElementMap?.[param];
      if (slider && /lower|upper/i.test(param)) {
        if (/upper/i.test(param)) {
          slider.updateSliderValues({ upperUpdate: slider.max });
        } else {
          slider.updateSliderValues({ lowerUpdate: slider.min });
        }
      }
    } else if (typeof this[param] === 'string') {
      this[param] = '';
    }
    this.normalizeFsDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    this.syncFsRouteFromCurrentFilters();
    this.fsCdr.markForCheck();
  }

  private clearFsMagicFilter(param: string): void {
    const identity = this.fsMagicFilterKeys.get(param);
    if (!identity) return;
    const label = identity.filterType === 'range_slider' ? param.slice(5) : param;
    if (identity.filterType === 'range_slider') {
      delete this.sliderValues[label];
      this.genericConfigSliders?.find(slider => slider.sliderConfig?.label === label)?.reset();
    } else if (identity.filterType === 'text_input') {
      delete this.configTextInputValues[label];
      this.fsMagicModules.flatMap(module => module.groups)
        .flatMap(group => group.filters)
        .filter(filter => filter.label === label)
        .forEach(filter => filter.value = '');
    } else {
      delete this.configMultiSelectValues[label];
      this.fsMagicModules.flatMap(module => module.groups)
        .flatMap(group => group.filters)
        .filter(filter => filter.label === label)
        .forEach(filter => (filter.options || []).forEach(option => option.checked = false));
    }
    const confidenceKey = `confidence:${label}Aira${identity.questionId}`;
    delete this.confidenceValues[confidenceKey];
    this.confidenceSliders?.find(slider => slider.formName === confidenceKey)?.reset();
  }

  private clearFsSlider(label: string): void {
    delete this.sliderValues[label];
    this.genericConfigSliders?.find(slider =>
      slider.formName === label || slider.sliderConfig?.label === label)?.reset();
  }

  fsTagValue(tag: FsFilterTag): string {
    const value = tag.value;
    if (this.fsMagicFilterKeys.has(tag.param)) {
      try {
        const selected = JSON.parse(String(value)).value;
        return Array.isArray(selected) ? selected.join(', ') : String(selected);
      } catch (_) { return ''; }
    }
    if (value === true || value === 'true') return '';
    if (tag.param === 'selectedTerritory') {
      if (value && typeof value === 'object') return String((value as any).name || '');
      try { return JSON.parse(String(value))?.name || ''; } catch (_) { return ''; }
    }
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          if (tag.param === 'selectedCustomColumns') return `${parsed.length} workspace ${parsed.length === 1 ? 'field' : 'fields'}`;
          if (tag.param === 'selectedAccountants' || tag.param === 'selectedAuditors' || tag.param === 'selectedBankers') {
            return parsed.map(supplier => supplier.canonical_name || supplier.name || supplier.id).join(', ');
          }
          if (tag.param === 'tagPositiveIdsSelected' || tag.param === 'tagNegativeIdsSelected') {
            return `${parsed.length} ${parsed.length === 1 ? 'tag' : 'tags'}`;
          }
          return parsed.join(', ');
        }
      } catch (_) { /* Fall through to the raw value. */ }
    }
    const text = String(value);
    return text.length > 50 ? text.slice(0, 47) + '…' : text;
  }

  fsCompanyNumber(company: Record<string, any>): string {
    return String(company['Number'] ?? company.number ?? '');
  }

  fsCompanyName(company: Record<string, any>): string {
    return String(company['Company Name'] || 'Company');
  }

  fsDisplayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not available';
    return String(value);
  }

  fsRevenue(company: Record<string, any>): string {
    const raw = company['Revenue'];
    if (raw === null || raw === undefined || raw === '') return 'Not available';
    const numeric = typeof raw === 'number' ? raw : Number(String(raw).replace(/[,£]/g, ''));
    return Number.isFinite(numeric)
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(numeric)
      : String(raw);
  }

  fsRevenueEstimated(company: Record<string, any>): boolean {
    return company['Is Revenue Estimated?'] === true ||
      company['Is Revenue Estimated?'] === 'true';
  }

  fsResultInsights(company: Record<string, any>): FsResultInsight[] {
    if (this.fsResultView === 'overview') return [];
    const insights = company['_financialServicesInsights']?.[this.fsResultView];
    return Array.isArray(insights) ? insights.filter(insight =>
      insight && insight.value !== null && insight.value !== undefined && insight.value !== '') : [];
  }

  fsResultContext(company: Record<string, any>): Array<{ label: string; value: string }> {
    if (this.fsResultView !== 'fx_trade') return [];
    return [
      { label: 'Imports', value: this.fsInsightValue(company['Basic Imports']) },
      { label: 'Exports', value: this.fsInsightValue(company['Basic Exports']) },
      { label: 'Trading countries', value: this.fsInsightValue(company['Countries of Trading Addresses']) },
    ].filter(item => item.value !== 'Not available');
  }

  /** Answer fields ending in "_gbp" are pound amounts. */
  fsInsightDisplay(insight: FsResultInsight): string {
    return /_gbp$/.test(insight.fieldKey || '') && typeof insight.value === 'number'
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(insight.value)
      : this.fsInsightValue(insight.value);
  }

  fsInsightValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not available';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);
    if (typeof value === 'string') return value.trim() || 'Not available';
    if (Array.isArray(value)) {
      const parts = value.map(item => this.fsInsightValue(item)).filter(item => item !== 'Not available');
      return parts.length ? parts.join(' · ') : 'Not available';
    }
    if (typeof value === 'object') {
      const parts = Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !['confidence', 'date_generated', 'source'].includes(key))
        .map(([key, item]) => {
          const display = this.fsInsightValue(item);
          return display === 'Not available' ? '' : `${key.replace(/_/g, ' ')}: ${display}`;
        }).filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Not available';
    }
    return String(value);
  }

  openFullDashboard(): void {
    this.normalizeFsDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    const visible = fsWorkspaceFilterKeys();
    const saved = JSON.parse(this.getJSONParamsToSave(false));
    const queryParams: Record<string, any> = {};
    Object.keys(saved).forEach(key => {
      if (visible.has(key) || this.isActiveFsMagicTag({ param: key, value: saved[key] })) {
        queryParams[key] = saved[key];
      }
    });
    this.fsRouter.navigate(['/dashboard'], {
      queryParams,
      info: { dashboardModeSwitch: true },
    });
  }

  makeFsDefault(): void {
    if (this.fsDefaultState === 'saving') return;
    this.fsDefaultState = 'saving';
    this.fsAuthenticationService.setDefaultSkin('financial_services').subscribe({
      next: () => {
        this.fsDefaultState = 'saved';
        this.fsCdr.markForCheck();
      },
      error: () => {
        this.fsDefaultState = 'error';
        this.fsCdr.markForCheck();
      },
    });
  }

  saveFsSearch(): void {
    this.normalizeFsDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    if (this.fsHasSearchCriterion) super.saveSearch();
  }

  override updateAfterSearchHasBeenSaved(data: Record<string, any>): void {
    super.updateAfterSearchHasBeenSaved(data);
    if (data?.success) this.fsSaveOpen = false;
  }

  override ngOnDestroy(): void {
    clearTimeout(this.fsCriteriaRefreshTimer);
    super.ngOnDestroy();
  }
}
